import { components } from "@/api-schema";
import { cn } from "@/common/helpers/utils";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command";
import { inputVariants } from "@/components/ui/input";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];
type AddressCityGroup = components["schemas"]["AddressCityGroup"];

export enum PickupLocationType {
  Branch = "Branch",
  City = "City",
}

export type PickupLocation =
  | { type: PickupLocationType.Branch; uuid: string }
  | { type: PickupLocationType.City; city: string }
  | null;

interface SearchableLocationSelectorProps {
  pickupBranches: Branch[];
  addressGroups: AddressCityGroup[];
  selectedBranch: string | null;
  selectedCity: string | null;
  onLocationChange: (location: PickupLocation) => void;
  className?: string;
  isLoading?: boolean;
}

export function SearchableLocationSelector({
  pickupBranches,
  addressGroups,
  selectedBranch,
  selectedCity,
  onLocationChange,
  className,
  isLoading = false,
}: SearchableLocationSelectorProps) {
  const { t } = useTranslation(["fit"]);
  const [open, setOpen] = useState(false);

  const hasLocations = pickupBranches.length > 0 || addressGroups.length > 0;

  const selectedLabel = () => {
    if (selectedBranch) {
      const branch = pickupBranches.find((b) => b.uuid === selectedBranch);
      return branch ? branch.name : t("fit:SelectLocation");
    }

    if (selectedCity) return selectedCity;
    if (isLoading) return t("fit:LocationsLoading");

    return t("fit:SelectLocation");
  };

  const handleSelectBranch = (uuid: string) => {
    onLocationChange({ type: PickupLocationType.Branch, uuid });
    setOpen(false);
  };

  const handleSelectCity = (group: AddressCityGroup) => {
    onLocationChange({ type: PickupLocationType.City, city: group.city });
    setOpen(false);
  };

  if (isLoading || !hasLocations) {
    const displayText = isLoading ? t("fit:LocationsLoading") : t("fit:NoLocationsAvailable");

    return (
      <Button
        variant={ButtonVariant.Ghost}
        colorVariant={ButtonColorVariant.Secondary}
        disabled
        className={cn(
          inputVariants(),
          "h-9 min-w-0 flex-1 justify-between px-3 font-normal opacity-50",
          "hover:bg-accent hover:text-accent-foreground",
          className,
        )}
      >
        <span>{displayText}</span>
        {isLoading ? (
          <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} className="ml-2 shrink-0 opacity-50" />
        ) : (
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        )}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={ButtonVariant.Input}
          colorVariant={ButtonColorVariant.Secondary}
          role="combobox"
          aria-expanded={open}
          className={cn(
            inputVariants(),
            "h-9 min-w-0 flex-1 justify-between px-3 font-normal",
            "hover:bg-accent hover:text-accent-foreground",
            className,
          )}
        >
          <span className="truncate">{selectedLabel()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-75 p-0" align="start">
        <Command>
          <CommandInput placeholder={t("fit:SearchLocation")} className="h-9" />
          <CommandEmpty>{t("fit:NoLocationFound")}</CommandEmpty>

          <div className="max-h-72 overflow-y-auto">
            {pickupBranches.length > 0 && (
              <CommandGroup heading={t("fit:Branches")}>
                {pickupBranches.map((branch) => {
                  const label = branch.name;

                  return (
                    <CommandItem
                      key={branch.uuid}
                      value={label}
                      onSelect={() => handleSelectBranch(branch.uuid)}
                      className="flex items-center justify-between"
                    >
                      <span>{label}</span>
                      {selectedBranch === branch.uuid && <Check className={cn("ml-2 h-4 w-4 shrink-0")} />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {addressGroups.length > 0 && (
              <CommandGroup heading={t("fit:ExternalAddresses")}>
                {addressGroups.map((group) => (
                  <CommandItem
                    key={group.city}
                    value={group.city}
                    onSelect={() => handleSelectCity(group)}
                    className="flex items-center justify-between"
                  >
                    <span>{group.city}</span>
                    {selectedCity === group.city && <Check className={cn("ml-2 h-4 w-4 shrink-0")} />}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
